/**
 * Environment Configuration for TimeLynxx Admin Portal
 * Detects deployment environment and configures app behavior
 */

export type Environment = 'development' | 'test' | 'production' | 'local';

export interface EnvironmentConfig {
  environment: Environment;
  isDevelopment: boolean;
  isProduction: boolean;
  apiUrl: string;
  subdomain: string;
  enableDebugMode: boolean;
  enableAnalytics: boolean;
  logLevel: 'debug' | 'info' | 'warn' | 'error';
  features: {
    devTools: boolean;
    mockData: boolean;
    performanceMetrics: boolean;
  };
}

/**
 * Detect current environment based on hostname and build settings
 */
function detectEnvironment(): Environment {
  // Check for Vite build-time environment variable first
  if (import.meta.env.VITE_ENVIRONMENT) {
    return import.meta.env.VITE_ENVIRONMENT as Environment;
  }

  // Fallback to hostname detection
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    if (hostname.includes('admin-dev.timelynxx.com')) {
      return 'development';
    }
    
    if (hostname.includes('admin-test.timelynxx.com')) {
      return 'test';
    }
    
    if (hostname.includes('admin.timelynxx.com')) {
      return 'production';
    }
    
    if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
      return 'local';
    }
  }

  // Default to development for safety
  return 'development';
}

/**
 * Get environment-specific configuration
 */
function getEnvironmentConfig(env: Environment): EnvironmentConfig {
  const baseConfig = {
    environment: env,
    isDevelopment: env === 'development' || env === 'local',
    isProduction: env === 'production',
  };

  switch (env) {
    case 'local':
      return {
        ...baseConfig,
        apiUrl: 'http://localhost:3000',
        subdomain: 'localhost',
        enableDebugMode: true,
        enableAnalytics: false,
        logLevel: 'debug' as const,
        features: {
          devTools: true,
          mockData: true,
          performanceMetrics: true,
        },
      };

    case 'development':
      return {
        ...baseConfig,
        apiUrl: 'https://admin-dev.timelynxx.com',
        subdomain: 'admin-dev',
        enableDebugMode: true,
        enableAnalytics: false,
        logLevel: 'debug' as const,
        features: {
          devTools: true,
          mockData: false,
          performanceMetrics: true,
        },
      };

    case 'test':
      return {
        ...baseConfig,
        apiUrl: 'https://admin-test.timelynxx.com',
        subdomain: 'admin-test',
        enableDebugMode: false,
        enableAnalytics: false,
        logLevel: 'info' as const,
        features: {
          devTools: false,
          mockData: false,
          performanceMetrics: true,
        },
      };

    case 'production':
      return {
        ...baseConfig,
        apiUrl: 'https://admin.timelynxx.com',
        subdomain: 'admin',
        enableDebugMode: false,
        enableAnalytics: true,
        logLevel: 'error' as const,
        features: {
          devTools: false,
          mockData: false,
          performanceMetrics: false,
        },
      };

    default:
      return getEnvironmentConfig('development');
  }
}

// Export the current environment configuration
export const ENVIRONMENT_CONFIG = getEnvironmentConfig(detectEnvironment());

/**
 * Environment-aware logging utility
 */
export const envLog = {
  debug: (message: string, data?: any) => {
    if (ENVIRONMENT_CONFIG.logLevel === 'debug') {
      console.log(`[DEBUG] ${message}`, data || '');
    }
  },
  info: (message: string, data?: any) => {
    if (['debug', 'info'].includes(ENVIRONMENT_CONFIG.logLevel)) {
      console.info(`[INFO] ${message}`, data || '');
    }
  },
  warn: (message: string, data?: any) => {
    if (['debug', 'info', 'warn'].includes(ENVIRONMENT_CONFIG.logLevel)) {
      console.warn(`[WARN] ${message}`, data || '');
    }
  },
  error: (message: string, data?: any) => {
    console.error(`[ERROR] ${message}`, data || '');
  },
};

/**
 * Check if a feature is enabled in the current environment
 */
export const isFeatureEnabled = (feature: keyof EnvironmentConfig['features']): boolean => {
  return ENVIRONMENT_CONFIG.features[feature];
};